import { jsPDF } from 'jspdf';
import { downloadBlob } from '../../toolPageUtils';
import { encodeCanvas } from '../../image/shared/imageOutputFormats';
import { EXPORT_PAGE_WIDTH_PX, ageCalculatorSheetStyles } from './ageCalculatorTemplate';

/**
 * Print, PDF and picture all come from the same printable sheet
 * (ageCalculatorTemplate) — the way the বাতা tracking tool exports — instead of
 * a screenshot of the on-screen preview.
 */

const EXPORT_SCALE = 2;
const A4 = { widthMm: 210, heightMm: 297, marginMm: 12 };

/** Wait for the sheet's web font and logo, so nothing renders in a fallback. */
async function waitForAssets(doc, root = doc) {
    try {
        await doc.fonts?.ready;
    } catch {
        // ignore font load errors
    }
    const images = Array.from(root.querySelectorAll('img'));
    await Promise.all(images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
        });
    }));
}

/** Write the sheet into a hidden same-origin iframe; the caller removes it. */
function mountSheet(html) {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;visibility:hidden';
    document.body.appendChild(iframe);

    const frameWindow = iframe.contentWindow;
    const frameDoc = frameWindow?.document;
    if (!frameDoc || !frameWindow) {
        iframe.remove();
        throw new Error('export frame unavailable');
    }
    frameDoc.open();
    frameDoc.write(html);
    frameDoc.close();
    return { iframe, frameWindow, frameDoc };
}

/**
 * Print the sheet through a hidden iframe — the browser's own print dialog,
 * which can also save it as a PDF, with the sheet's page-break rules.
 * @param {string} html from buildAgeCalculatorPrintHtml
 */
export function printAgeCalculatorSheet(html) {
    const { iframe, frameWindow, frameDoc } = mountSheet(html);
    const cleanup = () => iframe.remove();
    frameWindow.addEventListener('afterprint', cleanup, { once: true });

    let printed = false;
    const triggerPrint = () => {
        if (printed) return;
        printed = true;
        frameWindow.focus();
        frameWindow.print();
        setTimeout(cleanup, 60000);
    };

    void waitForAssets(frameDoc).then(triggerPrint);
    setTimeout(triggerPrint, 1500);
}

/** Load an image without `decode()` — it can reject on a large SVG data URL and hang. */
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const timer = setTimeout(() => reject(new Error('export timed out')), 20000);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('export render failed')); };
        img.src = url;
    });
}

/**
 * The picture and the PDF are painted by the browser itself: html-to-image puts
 * the sheet in an SVG `<foreignObject>`, so text sits in its cards, chips and
 * pills exactly as in print. html2canvas lays Bangla text out on its own and
 * dropped every line to the bottom of its box.
 *
 * The sheet is mounted in the app's page (its styles are scoped to `.sk-doc`),
 * so it uses the app's 'Hind Siliguri' with the Noto digits.
 *
 * @param {string} html from buildAgeCalculatorPrintHtml
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function captureAgeCalculatorSheet(html) {
    const { toSvg } = await import('html-to-image');
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('.sk-doc');
    if (!source) throw new Error('export sheet missing');

    const wrapper = document.createElement('div');
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.style.cssText = `position:fixed;left:-20000px;top:0;width:${EXPORT_PAGE_WIDTH_PX + 32}px;pointer-events:none;`;
    const style = document.createElement('style');
    style.textContent = `${ageCalculatorSheetStyles()}
        .sk-doc { padding: 16px; width: ${EXPORT_PAGE_WIDTH_PX + 32}px; max-width: none; }`;
    wrapper.appendChild(style);
    const sheet = document.importNode(source, true);
    wrapper.appendChild(sheet);
    document.body.appendChild(wrapper);

    try {
        await waitForAssets(document, sheet);
        const width = Math.ceil(sheet.offsetWidth);
        const height = Math.ceil(sheet.offsetHeight);
        const svgUrl = await toSvg(sheet, { width, height, backgroundColor: '#ffffff' });
        const img = await loadImage(svgUrl);

        const canvas = document.createElement('canvas');
        canvas.width = width * EXPORT_SCALE;
        canvas.height = height * EXPORT_SCALE;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas;
    } finally {
        wrapper.remove();
    }
}

/**
 * The sheet as a picture in the format picked in the action bar.
 * `ext` is what the file really is — a browser that cannot write the chosen
 * format falls back inside encodeCanvas, and the name follows the fallback.
 *
 * @param {string} html
 * @param {'jpg'|'png'|'webp'|'avif'} format
 * @param {string} baseName file name without extension
 * @returns {Promise<string>} the extension actually written
 */
export async function downloadAgeCalculatorImageAs(html, format, baseName = 'age-calculator') {
    const canvas = await captureAgeCalculatorSheet(html);
    const { blob, ext } = await encodeCanvas(canvas, format, { quality: 0.95 });
    downloadBlob(blob, `${baseName}.${ext}`);
    return ext;
}

/**
 * The sheet on A4 with the print margins. It is laid out at the printable width,
 * so it maps 1:1 across the page; a sheet taller than one page (comparison,
 * rashifol and the day's event all on) continues on the next.
 *
 * @param {string} html
 * @param {string} filename
 */
export async function downloadAgeCalculatorPdf(html, filename = 'age-calculator.pdf') {
    const canvas = await captureAgeCalculatorSheet(html);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const contentWidth = A4.widthMm - A4.marginMm * 2;
    const contentHeight = A4.heightMm - A4.marginMm * 2;
    const pxPerMm = canvas.width / contentWidth;
    const pageHeightPx = Math.floor(contentHeight * pxPerMm);

    for (let top = 0, page = 0; top < canvas.height; top += pageHeightPx, page += 1) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - top);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = sliceHeight;
        const ctx = slice.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, top, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

        if (page > 0) pdf.addPage();
        pdf.addImage(
            slice.toDataURL('image/jpeg', 0.95),
            'JPEG',
            A4.marginMm,
            A4.marginMm,
            contentWidth,
            sliceHeight / pxPerMm,
            undefined,
            'FAST',
        );
    }

    downloadBlob(pdf.output('blob'), filename);
}
